import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { interviewAPI } from '../services/api';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import toast from 'react-hot-toast';

export default function InterviewRoom() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const interview = location.state?.interview;
  const mode = interview?.mode || 'text_chat';

  const [currentQuestion, setCurrentQuestion] = useState(location.state?.firstQuestion || null);
  const [questionNum, setQuestionNum] = useState(1);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedMCQ, setSelectedMCQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [lastEval, setLastEval] = useState(null);

  const { isRecording, audioBlob, duration, error: recError, startRecording, stopRecording, resetRecording } = useVoiceRecorder();

  useEffect(() => {
    if (currentQuestion) {
      setMessages(prev => [...prev, { type: 'ai', text: currentQuestion.text, category: currentQuestion.category }]);
    }
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  // ─── Submit text answer ───
  const submitTextAnswer = async () => {
    if (!inputText.trim()) return toast.error('Please type your answer');
    const answer = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { type: 'user', text: answer }]);
    setLoading(true);

    try {
      const { data } = await interviewAPI.submitAnswer(id, { question_number: questionNum, answer_text: answer });
      handleEvaluation(data);
    } catch (err) {
      toast.error('Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  // ─── Submit MCQ answer ───
  const submitMCQAnswer = async (index) => {
    setSelectedMCQ(index);
    setLoading(true);
    const options = currentQuestion.options || [];
    setMessages(prev => [...prev, { type: 'user', text: `Selected: ${options[index]}` }]);

    try {
      const { data } = await interviewAPI.submitAnswer(id, { question_number: questionNum, answer_text: options[index], selected_mcq_index: index });
      handleEvaluation(data);
    } catch (err) {
      toast.error('Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  // ─── Submit voice answer ───
  const submitVoiceAnswer = async () => {
    if (!audioBlob) return toast.error('Please record your answer first');
    setLoading(true);
    setMessages(prev => [...prev, { type: 'user', text: '🎙️ Voice answer recorded', isVoice: true }]);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'answer.webm');
      formData.append('question_number', questionNum);
      const { data } = await interviewAPI.submitVoiceAnswer(id, formData);
      if (data.transcribed_text) {
        setMessages(prev => [...prev, { type: 'system', text: `Transcription: "${data.transcribed_text}"` }]);
      }
      handleEvaluation(data);
    } catch (err) {
      toast.error('Failed to process voice answer');
    } finally {
      setLoading(false);
      resetRecording();
    }
  };

  // ─── Handle AI evaluation response ───
  const handleEvaluation = (data) => {
    const eval_ = data.evaluation;
    setLastEval(eval_);

    let feedbackMsg = `Score: ${eval_.score}/10\n\n${eval_.feedback}`;
    if (eval_.strengths?.length) feedbackMsg += `\n\n✅ Strengths: ${eval_.strengths.join(', ')}`;
    if (eval_.improvements?.length) feedbackMsg += `\n\n💡 Improve: ${eval_.improvements.join(', ')}`;
    if (eval_.speech_feedback) feedbackMsg += `\n\n🎙️ Speech: ${eval_.speech_feedback}`;

    setMessages(prev => [...prev, { type: 'ai', text: feedbackMsg, isEval: true, score: eval_.score }]);

    if (data.is_last_question) {
      setFinished(true);
      setMessages(prev => [...prev, { type: 'system', text: 'Interview complete! Generating your report...' }]);
    } else if (data.next_question) {
      setTimeout(() => {
        setCurrentQuestion(data.next_question);
        setQuestionNum(data.next_question.number);
        setSelectedMCQ(null);
        setMessages(prev => [...prev, {
          type: 'ai',
          text: data.next_question.text,
          category: data.next_question.category
        }]);
      }, 1500);
    }
  };

  // ─── End interview ───
  const handleEndInterview = async () => {
    setLoading(true);
    try {
      await interviewAPI.end(id);
      toast.success('Report generated!');
      navigate(`/interview/${id}/report`);
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (s) => {
    if (s >= 8) return 'var(--success)';
    if (s >= 6) return 'var(--primary)';
    if (s >= 4) return 'var(--accent)';
    return 'var(--danger)';
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Header */}
      <div className="chat-header">
        <div>
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {interview?.job_role || 'Interview'} — <span className="tag tag-primary">{mode.replace('_', ' ')}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Question {questionNum} of {interview?.total_questions || '?'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Progress bar */}
          <div className="progress-bar" style={{ width: 200 }}>
            {Array.from({ length: interview?.total_questions || 10 }, (_, i) => (
              <div key={i} className={`progress-step ${i < questionNum - 1 ? 'done' : i === questionNum - 1 ? 'current' : ''}`} />
            ))}
          </div>
          {finished ? (
            <button className="btn btn-success" onClick={handleEndInterview} disabled={loading}>
              {loading ? <span className="spinner" /> : 'View Report →'}
            </button>
          ) : (
            <button className="btn btn-sm btn-danger" onClick={handleEndInterview}>End Early</button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`chat-message ${msg.type === 'user' ? 'user' : msg.type === 'system' ? '' : 'ai'}`}
            style={msg.type === 'system' ? { alignSelf: 'center', background: 'var(--accent-light)', color: 'var(--text)', fontSize: 13, borderRadius: 'var(--radius-full)', padding: '8px 20px' } : {}}>
            {msg.category && <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>{msg.category}</div>}
            {msg.isEval && <div style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: scoreColor(msg.score), color: '#fff', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{msg.score}/10</div>}
            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
          </div>
        ))}
        {loading && (
          <div className="chat-message ai" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="spinner" /> Evaluating your answer...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {!finished && (
        <div className="chat-input-area">
          {mode === 'text_chat' && (
            <>
              <textarea className="chat-textarea" placeholder="Type your answer here..." value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitTextAnswer(); } }}
                rows={2} disabled={loading} />
              <button className="btn btn-primary" onClick={submitTextAnswer} disabled={loading || !inputText.trim()}>
                Send
              </button>
            </>
          )}

          {mode === 'mcq' && currentQuestion?.options && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {currentQuestion.options.map((opt, i) => (
                <div key={i} className={`mcq-option ${selectedMCQ === i ? 'selected' : ''}`}
                  onClick={() => !loading && selectedMCQ === null && submitMCQAnswer(i)}
                  style={{ pointerEvents: loading || selectedMCQ !== null ? 'none' : 'auto' }}>
                  <span className="mcq-label">{String.fromCharCode(65 + i)}</span>
                  <span>{opt}</span>
                </div>
              ))}
            </div>
          )}

          {mode === 'voice' && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
              {recError && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{recError}</div>}
              <div className="voice-recorder" style={{ padding: 16 }}>
                <div className="record-timer">{formatTime(duration)}</div>
                {!audioBlob ? (
                  <button className={`record-btn ${isRecording ? 'recording' : 'idle'}`}
                    onClick={isRecording ? stopRecording : startRecording}>
                    {isRecording ? '⏹' : '🎙️'}
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button className="btn btn-secondary" onClick={resetRecording}>Re-record</button>
                    <button className="btn btn-primary" onClick={submitVoiceAnswer} disabled={loading}>
                      {loading ? <span className="spinner" /> : 'Submit Answer'}
                    </button>
                  </div>
                )}
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {isRecording ? 'Recording... click to stop' : audioBlob ? 'Ready to submit' : 'Click to start recording'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
